import api from "./client";
import type {
  DiscussionTab,
  Discussion,
  ChatMessage,
  ChatAttachment,
  MessageChannel,
  ReadReceipt,
} from "../../types/messaging";

export interface ClientMuteState {
  dossier_id: string | null;
  muted: boolean;
  muted_at?: string | null;
  muted_by?: string | null;
  muted_by_name?: string | null;
  muted_reason?: string | null;
  unmuted_at?: string | null;
  unmuted_by_name?: string | null;
  unmuted_reason?: string | null;
}

export interface UnreadMemberEntry {
  user_id: string;
  user_name: string;
  unread_discussions: number;
}

export interface UnreadOverview {
  /** Conversations ou le dernier message du client n'a ete lu par personne. */
  client_unread_discussions: number;
  /** Conversations non lues par au moins un membre. */
  total_unread_discussions: number;
  by_member: UnreadMemberEntry[];
  scanned_discussions: number;
  window_days: number;
  computed_at: string;
}

export const messagerieApi = {
  // === Discussions ===

  listDiscussions: async (params?: {
    tab?: DiscussionTab;
    limit?: number;
    offset?: number;
  }): Promise<{ items: Discussion[]; total: number }> => {
    const resp = await api.get("/messagerie/discussions", { params });
    return { items: resp.data, total: parseInt(resp.headers["x-total-count"] || "0", 10) };
  },

  /** Vue d'ensemble des non-lus de l'organisation (widget d'accueil). */
  getUnreadOverview: async (days?: number): Promise<UnreadOverview> => {
    const { data } = await api.get("/messagerie/unread-overview", {
      params: days ? { days } : undefined,
    });
    return data;
  },

  getDiscussion: async (id: string): Promise<Discussion> => {
    const { data } = await api.get(`/messagerie/discussions/${id}`);
    return data;
  },

  createDiscussion: async (payload: {
    tab: DiscussionTab;
    name: string;
    description?: string;
    member_ids: string[];
    project_id?: string;
  }): Promise<Discussion> => {
    const { data } = await api.post("/messagerie/discussions", payload);
    return data;
  },

  updateDiscussion: async (
    id: string,
    payload: { name?: string; description?: string },
  ): Promise<Discussion> => {
    const { data } = await api.patch(`/messagerie/discussions/${id}`, payload);
    return data;
  },

  /** Set/override the WhatsApp recipient number for a discussion.
   *  When updateContact is true, also updates the dossier's primary contact phone. */
  setWhatsappPhone: async (
    id: string,
    phone: string,
    updateContact: boolean,
  ): Promise<Discussion> => {
    const { data } = await api.post(`/messagerie/discussions/${id}/whatsapp-phone`, {
      phone,
      update_contact: updateContact,
    });
    return data;
  },

  deleteDiscussion: async (id: string): Promise<void> => {
    await api.delete(`/messagerie/discussions/${id}`);
  },

  // === Members ===

  addMembers: async (
    discussionId: string,
    userIds: string[],
  ): Promise<Discussion> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/members`,
      { user_ids: userIds },
    );
    return data;
  },

  removeMember: async (
    discussionId: string,
    userId: string,
  ): Promise<void> => {
    await api.delete(
      `/messagerie/discussions/${discussionId}/members/${userId}`,
    );
  },

  muteDiscussion: async (
    discussionId: string,
    mute: boolean,
  ): Promise<void> => {
    await api.post(`/messagerie/discussions/${discussionId}/mute`, { mute });
  },

  /** Sourdine COMPLETE du client (dossier) : plus aucun email ni WhatsApp ne lui
   *  part, automatique comme manuel. A ne pas confondre avec muteDiscussion,
   *  qui ne coupe que les notifications internes de l'utilisateur courant. */
  getClientMute: async (discussionId: string): Promise<ClientMuteState> => {
    const { data } = await api.get(`/messagerie/discussions/${discussionId}/client-mute`);
    return data;
  },

  setClientMute: async (
    discussionId: string,
    muted: boolean,
    reason?: string,
  ): Promise<ClientMuteState> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/client-mute`,
      { muted, reason },
    );
    return data;
  },

  // === Messages ===

  listMessages: async (
    discussionId: string,
    params?: { limit?: number; before?: string },
  ): Promise<ChatMessage[]> => {
    const { data } = await api.get(
      `/messagerie/discussions/${discussionId}/messages`,
      { params },
    );
    return data;
  },

  searchMessages: async (
    discussionId: string,
    query: string,
    limit = 50,
  ): Promise<ChatMessage[]> => {
    const { data } = await api.get(
      `/messagerie/discussions/${discussionId}/messages/search`,
      { params: { q: query, limit } },
    );
    return data;
  },

  sendMessage: async (
    discussionId: string,
    payload: {
      text?: string;
      attachment?: ChatAttachment;
      attachments?: ChatAttachment[];
      folder_refs?: { id: string; name: string }[];
      reply_to_id?: string;
      mentions?: string[];
      channel?: MessageChannel;
      poll?: { question: string; options: string[]; multi: boolean; anonymous: boolean };
    },
  ): Promise<ChatMessage> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/messages`,
      payload,
    );
    return data;
  },

  updateMessage: async (
    discussionId: string,
    messageId: string,
    text: string,
  ): Promise<ChatMessage> => {
    const { data } = await api.patch(
      `/messagerie/discussions/${discussionId}/messages/${messageId}`,
      { text },
    );
    return data;
  },

  deleteMessage: async (
    discussionId: string,
    messageId: string,
  ): Promise<void> => {
    await api.delete(
      `/messagerie/discussions/${discussionId}/messages/${messageId}`,
    );
  },

  /** Re-send a WhatsApp message that previously failed (wa_status === "failed"). */
  retryMessage: async (
    discussionId: string,
    messageId: string,
  ): Promise<ChatMessage> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/messages/${messageId}/retry`,
    );
    return data;
  },

  toggleReaction: async (
    discussionId: string,
    messageId: string,
    emoji: string,
  ): Promise<ChatMessage> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/messages/${messageId}/reactions`,
      { emoji },
    );
    return data;
  },

  /** Pin a message — backend returns the updated discussion (with pinned_messages). */
  pinMessage: async (
    discussionId: string,
    messageId: string,
  ): Promise<Discussion> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/messages/${messageId}/pin`,
    );
    return data;
  },

  /** Unpin a message — backend returns the updated discussion (with pinned_messages). */
  unpinMessage: async (
    discussionId: string,
    messageId: string,
  ): Promise<Discussion> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/messages/${messageId}/unpin`,
    );
    return data;
  },

  // === Read receipts ===

  votePoll: async (
    discussionId: string,
    messageId: string,
    optionId: string,
  ): Promise<ChatMessage> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/messages/${messageId}/vote`,
      { option_id: optionId },
    );
    return data;
  },

  closePoll: async (discussionId: string, messageId: string): Promise<ChatMessage> => {
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/messages/${messageId}/poll/close`,
    );
    return data;
  },

  markAsRead: async (
    discussionId: string,
    lastReadMessageId: string,
  ): Promise<void> => {
    await api.post(`/messagerie/discussions/${discussionId}/read`, {
      last_read_message_id: lastReadMessageId,
    });
  },

  getReadReceipts: async (discussionId: string): Promise<ReadReceipt[]> => {
    const { data } = await api.get(`/messagerie/discussions/${discussionId}/read-receipts`);
    return data;
  },

  getUnreadCounts: async (): Promise<Record<string, number>> => {
    const { data } = await api.get("/messagerie/unread-counts");
    return data;
  },

  // === Follow / Unfollow ===

  followDiscussion: async (discussionId: string): Promise<void> => {
    await api.post(`/messagerie/discussions/${discussionId}/follow`);
  },

  unfollowDiscussion: async (discussionId: string): Promise<void> => {
    await api.post(`/messagerie/discussions/${discussionId}/unfollow`);
  },

  // === Inbound email ===

  checkEmailsNow: async (): Promise<{ processed: number; matched: number; unmatched: number }> => {
    const { data } = await api.post("/inbound-emails/check-now");
    return data;
  },

  // === File upload ===

  uploadAttachment: async (
    discussionId: string,
    file: File,
    onUploadProgress?: (progress: number) => void,
  ): Promise<{
    gcs_path: string;
    name: string;
    mime_type: string;
    size: number;
    preview_gcs_path?: string;
    pages?: number;
  }> => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post(
      `/messagerie/discussions/${discussionId}/upload`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onUploadProgress
          ? (e) => {
              if (e.total) {
                onUploadProgress(Math.round((e.loaded * 100) / e.total));
              }
            }
          : undefined,
      },
    );
    return data;
  },
};
